import logger from '~/services/logger';
import { twMerge } from 'tailwind-merge';
import { responsiveBreakpoints } from '~/services/layout/layout';
import {
  getCellVisibillityClasses,
  type ResponsiveSources,
} from './power-cell-getters';

type BaseParams = {
  imageSources: ResponsiveSources;
  hoverSources: ResponsiveSources;
  imageClassNames: string;
  imageClassNamesOnHover: string;
  imageParentClassNames: string;
  hoverClassNames: string;
  hoverParentClassNames: string;
  altText: string;
};

type ExclusiveParams =
  | {
      powerCellImage?: { isPowerCellImageHover?: boolean };
      isHoverImage?: never;
    }
  | { powerCellImage?: never; isHoverImage?: boolean };

type ResponsiveImageParams = BaseParams & ExclusiveParams;

const TRANSPARENT_IMAGE_SRC =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';

/**
 * Renders the image content for each breakpoint
 * @param imageSources - Image sources for each breakpoint
 * @param imageClassNames - Class names for the image
 * @param imageClassNamesOnHover - Class names for the image on hover
 * @param imageParentClassNames - Class names for the parent of image
 * @param hoverParentClassNames - Class names for the parent of hover
 * @param hoverSources - Hover sources for each breakpoint
 * @param hoverClassNames - Class names for the hover
 * @param altText - Alt text for the image
 * @returns - JSX.Element with responsive image
 */
export function ResponsiveImageWithHover({
  imageSources,
  imageClassNames,
  imageClassNamesOnHover,
  hoverSources,
  hoverClassNames,
  altText,
  imageParentClassNames,
  hoverParentClassNames,
}: ResponsiveImageParams): JSX.Element {
  try {
    return (
      <>
        <div
          className={imageParentClassNames}
          data-testid="ContentBadgeDefault"
        >
          <picture>
            {typeof imageSources.mobile === 'string' && (
              <source
                srcSet={imageSources.mobile}
                media={`(max-width: ${responsiveBreakpoints.tablet - 1}px)`}
              />
            )}
            {typeof imageSources.tablet === 'string' && (
              <source
                srcSet={imageSources.tablet}
                media={`(max-width: ${
                  responsiveBreakpoints.desktop - 1
                }px) and (min-width: ${responsiveBreakpoints.tablet}px)`}
              />
            )}
            {typeof imageSources.desktop === 'string' && (
              <source
                srcSet={imageSources.desktop}
                media={`(max-width: ${
                  responsiveBreakpoints['large-desktop'] - 1
                }px) and (min-width: ${responsiveBreakpoints.desktop}px)`}
              />
            )}
            {typeof imageSources['large-desktop'] === 'string' && (
              <source
                srcSet={imageSources['large-desktop']}
                media={`(min-width: ${responsiveBreakpoints['large-desktop']}px)`}
              />
            )}

            <img
              src={TRANSPARENT_IMAGE_SRC}
              alt={altText}
              className={twMerge(
                getCellVisibillityClasses(imageSources, imageClassNames),
                imageClassNamesOnHover
              )}
            />
          </picture>
        </div>

        <div className={hoverParentClassNames} data-testid="ContentBadgeHover">
          <picture>
            {typeof hoverSources.mobile === 'string' && (
              <source
                srcSet={hoverSources.mobile}
                media={`(max-width: ${responsiveBreakpoints.tablet - 1}px)`}
              />
            )}
            {typeof hoverSources.tablet === 'string' && (
              <source
                srcSet={hoverSources.tablet}
                media={`(max-width: ${
                  responsiveBreakpoints.desktop - 1
                }px) and (min-width: ${responsiveBreakpoints.tablet}px)`}
              />
            )}
            {typeof hoverSources.desktop === 'string' && (
              <source
                srcSet={hoverSources.desktop}
                media={`(max-width: ${
                  responsiveBreakpoints['large-desktop'] - 1
                }px) and (min-width: ${responsiveBreakpoints.desktop}px)`}
              />
            )}
            {typeof hoverSources['large-desktop'] === 'string' && (
              <source
                srcSet={hoverSources['large-desktop']}
                media={`(min-width: ${responsiveBreakpoints['large-desktop']}px)`}
              />
            )}

            <img
              src={TRANSPARENT_IMAGE_SRC}
              alt={altText}
              className={twMerge(
                getCellVisibillityClasses(hoverSources, hoverClassNames),
                'hidden group-hover:block'
              )}
            />
          </picture>
        </div>
      </>
    );
  } catch (error: any) {
    logger.info(`ResponsiveImage: ${error.message}`);
    return <></>;
  }
}
